"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Calendar as CalendarIcon, Clock, User, Mail, Phone, CircleCheck as CheckCircle2, ArrowRight, ArrowLeft, Sparkles, ShieldCheck, Check, Building, Globe, FileText, Layers, Search, Star, ChartLine as LineChart } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormField, FormItem, FormControl, FormMessage, FormLabel, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const SERVICES = [{
  id: "checkup",
  title: "Free AI Visibility Checkup",
  duration: "20 min",
  badge: "Most Popular",
  description: "Get a plain-English baseline audit showing how AI engines answer questions about your local business.",
  icon: Search
}, {
  id: "cleanup",
  title: "Website Cleanup & Landing Pages",
  duration: "45 min",
  badge: "Core Service",
  description: "Structure your business info and pages so AI web crawlers accurately extract your core services.",
  icon: Layers
}, {
  id: "reviews",
  title: "AI Review & Citation System",
  duration: "30 min",
  badge: "Growth Engine",
  description: "Automate real customer reviews and structured business citations across key local directories.",
  icon: Star
}, {
  id: "tracking",
  title: "Monthly Tracking & Reporting",
  duration: "30 min",
  badge: "Ongoing",
  description: "Continuous AI ranking checks, sentiment tracking, and straightforward monthly visibility reports.",
  icon: LineChart
}];
const TIME_SLOTS = ["09:00 AM", "10:30 AM", "01:00 PM", "02:30 PM", "04:00 PM"];
const STEPS = [{
  number: 1,
  title: "Service",
  description: "Select focus"
}, {
  number: 2,
  title: "Date & Time",
  description: "Pick slot"
}, {
  number: 3,
  title: "Business",
  description: "Company info"
}, {
  number: 4,
  title: "Contact",
  description: "Your details"
}, {
  number: 5,
  title: "Review",
  description: "Summary"
}, {
  number: 6,
  title: "Confirmation",
  description: "All set"
}];
function BookingFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  const {
    setValue
  } = useFormContext();
  const shouldReduceMotion = useReducedMotion();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([SERVICES[0].id]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(TIME_SLOTS[0]);
  const [businessName, setBusinessName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [cityArea, setCityArea] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const toggleServiceSelection = (id: string) => {
    setSelectedServiceIds(prev => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev;
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };
  const selectedServiceObjs = SERVICES.filter(s => selectedServiceIds.includes(s.id));
  const totalMinutes = selectedServiceObjs.reduce((acc, curr) => {
    const match = curr.duration.match(/\d+/);
    return acc + (match ? parseInt(match[0], 10) : 0);
  }, 0);
  const combinedDuration = `${totalMinutes} min`;
  useEffect(() => {
    const titles = selectedServiceObjs.map(s => s.title).join(", ");
    setValue("services", titles);
  }, [selectedServiceIds, setValue, selectedServiceObjs]);
  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const progressPercentage = currentStep / STEPS.length * 100;
  const formattedDate = selectedDate ? selectedDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric"
  }) : "Not selected";
  const selectedTitlesString = selectedServiceObjs.map(s => s.title).join(", ");
  if (isSubmitted && !submitError) {
    return <Card className="bg-card/80 border-border/60 text-card-foreground backdrop-blur-md shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-1 bg-[var(--accent-deep-red)]" />
        <CardContent className="p-8 md:p-12 text-center space-y-6">
          <div className="mx-auto size-16 rounded-full bg-[hsl(352_54%_33%/0.1)] text-[var(--accent-deep-red)] flex items-center justify-center border border-[hsl(352_54%_33%/0.3)]">
            <CheckCircle2 className="size-8" />
          </div>
          <div className="space-y-2">
            <span className="font-mono text-xs uppercase tracking-widest text-[var(--accent-deep-red)]">
              Booking Confirmed
            </span>
            <h3 className="text-2xl md:text-3xl font-bold font-default">
              You&apos;re All Set for Your Call
            </h3>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm md:text-base">
              We have reserved your slot for <strong className="text-foreground">{selectedTitlesString}</strong> on{" "}
              <strong className="text-foreground">{formattedDate}</strong> at <strong className="text-foreground">{selectedTimeSlot}</strong>.
            </p>
          </div>

          <Separator className="bg-border/60 my-6" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left max-w-md mx-auto bg-muted/30 p-4 rounded-lg border border-border/40">
            <div>
              <span className="text-xs text-muted-foreground block font-mono">CLIENT</span>
              <span className="text-sm font-semibold">{fullName || "Valued Client"}</span>
            </div>
            <div>
              <span className="text-xs text-muted-foreground block font-mono">COMPANY</span>
              <span className="text-sm font-semibold">{businessName || "Your Business"}</span>
            </div>
            <div>
              <span className="text-xs text-muted-foreground block font-mono">CONTACT</span>
              <span className="text-sm text-foreground/90">{email || "Email sent"}</span>
            </div>
            <div>
              <span className="text-xs text-muted-foreground block font-mono">DURATION</span>
              <span className="text-sm text-foreground/90">{combinedDuration}</span>
            </div>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="default" className="bg-[var(--accent-deep-red)] text-white hover:bg-[hsl(352_54%_28%)]" asChild>
              <Link to="/HomePage">Return to Homepage</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/ServicesPage">Explore Our Services</Link>
            </Button>
          </div>
        </CardContent>
      </Card>;
  }
  return <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-sm">
          <div className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
            Step {currentStep} of {STEPS.length}: <span className="text-foreground font-semibold">{STEPS[currentStep - 1].title}</span>
          </div>
          <div className="text-xs font-mono text-[var(--accent-deep-red)] flex items-center gap-1.5">
            <Sparkles className="size-3.5" />
            <span>Guaranteed 48-Hour Confirmation</span>
          </div>
        </div>
        <Progress value={progressPercentage} className="h-1.5 bg-muted/50 [&>div]:bg-[var(--accent-deep-red)]" />

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 pt-2">
          {STEPS.map((step, i) => {
          const isActive = step.number === currentStep;
          const isDone = step.number < currentStep;
          return <Button key={step.number} data-index={i} variant="ghost" onClick={() => {
            if (step.number < currentStep) {
              setCurrentStep(step.number);
            }
          }} disabled={step.number > currentStep} className={`flex flex-col items-start justify-center h-auto py-2 px-3 text-left border rounded-lg transition-all ${isActive ? "border-[var(--accent-deep-red)] bg-[hsl(352_54%_33%/0.1)] text-foreground" : isDone ? "border-border/60 bg-muted/20 text-muted-foreground hover:bg-muted/40 cursor-pointer" : "border-border/20 text-muted-foreground/40 opacity-60 cursor-not-allowed"}`}>
                <span className="font-mono text-[10px] uppercase tracking-wider flex items-center gap-1">
                  {isDone ? <Check className="size-3 text-[var(--accent-deep-red)]" /> : `0${step.number}`}
                </span>
                <span className="text-xs font-semibold truncate w-full">{step.title}</span>
              </Button>;
        })}
        </div>
      </div>

      <Card className="bg-card/90 border-border/60 text-card-foreground shadow-xl backdrop-blur-sm relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-[hsl(352_54%_33%/0.5)] to-transparent" />

        <CardHeader className="border-b border-border/40 pb-5">
          <CardTitle className="text-xl md:text-2xl font-bold font-default flex items-center justify-between">
            <span>{STEPS[currentStep - 1].title}</span>
            <span className="font-mono text-xs font-normal text-muted-foreground border border-border/60 px-2.5 py-1 rounded-full">
              {STEPS[currentStep - 1].description}
            </span>
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {currentStep === 1 && "Choose one or more call objectives that match your business goals."}
            {currentStep === 2 && "Select a convenient date and time for your strategy session."}
            {currentStep === 3 && "Tell us a bit about your service business location and online presence."}
            {currentStep === 4 && "Provide your contact details so we can send the meeting invite."}
            {currentStep === 5 && "Verify your appointment information before scheduling."}
            {currentStep === 6 && "Finalizing your booking request."}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6 pb-8">
          <Form onSubmit={handleSubmit}>
            <FormField control={control} name="services" render={({
            field
          }) => <input type="hidden" {...field} />} />

            <AnimatePresence mode="wait">
              {currentStep === 1 && <motion.div key="step1" initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={shouldReduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {SERVICES.map((srv, i) => {
                  const IconComp = srv.icon;
                  const isSelected = selectedServiceIds.includes(srv.id);
                  return <div key={srv.id} data-index={i} onClick={() => toggleServiceSelection(srv.id)} className={`p-5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between space-y-4 ${isSelected ? "border-[var(--accent-deep-red)] bg-[hsl(352_54%_33%/0.05)] ring-1 ring-[hsl(352_54%_33%/0.4)] shadow-md" : "border-border/60 bg-muted/10 hover:border-border hover:bg-muted/30"}`}>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="p-2.5 rounded-lg bg-background border border-border/60 text-[var(--accent-deep-red)]">
                                <IconComp className="size-5" />
                              </div>
                              <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded text-muted-foreground">
                                {srv.badge}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-base text-foreground">{srv.title}</h4>
                              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                {srv.description}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-2 border-t border-border/30 text-xs font-mono">
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Clock className="size-3.5 text-[var(--accent-deep-red)]" />
                              {srv.duration}
                            </span>
                            <span className={isSelected ? "text-[var(--accent-deep-red)] font-semibold flex items-center gap-1" : "text-muted-foreground"}>
                              {isSelected ? <>
                                  <Check className="size-3.5 text-[var(--accent-deep-red)]" />
                                  <span>Selected</span>
                                </> : "Select Service"}
                            </span>
                          </div>
                        </div>;
                })}
                  </div>

                  <div className="text-xs font-mono text-muted-foreground text-center sm:text-left pt-1">
                    Select one or more services — {selectedServiceIds.length} selected · {combinedDuration} total
                  </div>
                </motion.div>}

              {currentStep === 2 && <motion.div key="step2" initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={shouldReduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-7 flex flex-col items-center justify-center p-4 bg-muted/10 rounded-xl border border-border/40">
                    <div className="mb-2 w-full text-left font-mono text-xs text-muted-foreground flex items-center gap-1.5">
                      <SafeHydrate><CalendarIcon className="size-3.5 text-[var(--accent-deep-red)]" /></SafeHydrate>
                      <span>Select Preferred Date</span>
                    </div>
                    <SafeHydrate><Calendar mode="single" selected={selectedDate} onSelect={date => date && setSelectedDate(date)} className="rounded-md border border-border/40 bg-background/50" /></SafeHydrate>
                  </div>

                  <div className="lg:col-span-5 space-y-4">
                    <div className="font-mono text-xs text-muted-foreground flex items-center gap-1.5">
                      <Clock className="size-3.5 text-[var(--accent-deep-red)]" />
                      <span>Available Time Slots</span>
                    </div>

                    <div className="grid grid-cols-1 gap-2.5">
                      {TIME_SLOTS.map((slot, i) => {
                    const isSelected = selectedTimeSlot === slot;
                    return <Button key={slot} data-index={i} variant="outline" type="button" onClick={() => setSelectedTimeSlot(slot)} className={`w-full justify-between h-11 px-4 text-sm font-mono transition-all ${isSelected ? "border-[var(--accent-deep-red)] bg-[var(--accent-deep-red)] text-white shadow-sm hover:bg-[hsl(352_54%_28%)]" : "hover:border-[hsl(352_54%_33%/0.5)]"}`}>
                            <span>{slot}</span>
                            {isSelected && <Check className="size-4 text-white" />}
                          </Button>;
                  })}
                    </div>

                    <div className="p-3 rounded-lg bg-muted/20 border border-border/40 text-xs text-muted-foreground flex items-start gap-2 mt-4">
                      <ShieldCheck className="size-4 text-[var(--accent-deep-red)] shrink-0 mt-0.5" />
                      <span>All times are displayed in your local timezone. Instant confirmation provided upon submission.</span>
                    </div>
                  </div>
                </motion.div>}

              {currentStep === 3 && <motion.div key="step3" initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={shouldReduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="space-y-4">
                  <FormField control={control} name="businessName" rules={{
                required: "Business name is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="flex items-center gap-1.5">
                          <Building className="size-3.5 text-[var(--accent-deep-red)]" />
                          <span>Business Name</span>
                        </FormLabel>
                        <FormControl>
                          <Input className="autofill:bg-background autofill:text-foreground" placeholder="e.g. Apex Roofing & Solar" {...field} value={businessName} onChange={e => {
                    field.onChange(e);
                    setBusinessName(e.target.value);
                  }} />
                        </FormControl>
                        <FormDescription>The legal or trading name of your service business.</FormDescription>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="websiteUrl" render={({
                field
              }) => <FormItem>
                        <FormLabel className="flex items-center gap-1.5">
                          <Globe className="size-3.5 text-[var(--accent-deep-red)]" />
                          <span>Website URL</span>
                        </FormLabel>
                        <FormControl>
                          <Input className="autofill:bg-background autofill:text-foreground" placeholder="https://yourbusiness.com" {...field} value={websiteUrl} onChange={e => {
                    field.onChange(e);
                    setWebsiteUrl(e.target.value);
                  }} />
                        </FormControl>
                        <FormDescription>Optional — allows us to pre-audit your site prior to the call.</FormDescription>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="cityArea" render={({
                field
              }) => <FormItem>
                        <FormLabel className="flex items-center gap-1.5">
                          <FileText className="size-3.5 text-[var(--accent-deep-red)]" />
                          <span>City / Service Area</span>
                        </FormLabel>
                        <FormControl>
                          <Input className="autofill:bg-background autofill:text-foreground" placeholder="e.g. Austin, TX and surrounding metro" {...field} value={cityArea} onChange={e => {
                    field.onChange(e);
                    setCityArea(e.target.value);
                  }} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </motion.div>}

              {currentStep === 4 && <motion.div key="step4" initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={shouldReduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="space-y-4">
                  <FormField control={control} name="fullName" rules={{
                required: "Your name is required",
                minLength: {
                  value: 2,
                  message: "Min 2 characters"
                }
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="flex items-center gap-1.5">
                          <User className="size-3.5 text-[var(--accent-deep-red)]" />
                          <span>Full Name</span>
                        </FormLabel>
                        <FormControl>
                          <Input className="autofill:bg-background autofill:text-foreground" placeholder="John Doe" {...field} value={fullName} onChange={e => {
                    field.onChange(e);
                    setFullName(e.target.value);
                  }} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={control} name="email" rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email"
                  }
                }} render={({
                  field
                }) => <FormItem>
                          <FormLabel className="flex items-center gap-1.5">
                            <Mail className="size-3.5 text-[var(--accent-deep-red)]" />
                            <span>Email Address</span>
                          </FormLabel>
                          <FormControl>
                            <Input type="email" className="autofill:bg-background autofill:text-foreground" placeholder="john@example.com" {...field} value={email} onChange={e => {
                      field.onChange(e);
                      setEmail(e.target.value);
                    }} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />

                    <FormField control={control} name="phone" rules={{
                  required: "Phone number is required",
                  minLength: {
                    value: 7,
                    message: "Min 7 digits"
                  }
                }} render={({
                  field
                }) => <FormItem>
                          <FormLabel className="flex items-center gap-1.5">
                            <Phone className="size-3.5 text-[var(--accent-deep-red)]" />
                            <span>Phone Number</span>
                          </FormLabel>
                          <FormControl>
                            <Input type="tel" className="autofill:bg-background autofill:text-foreground" placeholder="(555) 000-0000" {...field} value={phone} onChange={e => {
                      field.onChange(e);
                      setPhone(e.target.value);
                    }} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>

                  <FormField control={control} name="notes" render={({
                field
              }) => <FormItem>
                        <FormLabel className="flex items-center gap-1.5">
                          <FileText className="size-3.5 text-[var(--accent-deep-red)]" />
                          <span>Additional Notes / Questions</span>
                        </FormLabel>
                        <FormControl>
                          <Input className="autofill:bg-background autofill:text-foreground" placeholder="Anything specific you want us to cover on the call?" {...field} value={notes} onChange={e => {
                    field.onChange(e);
                    setNotes(e.target.value);
                  }} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </motion.div>}

              {currentStep === 5 && <motion.div key="step5" initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={shouldReduceMotion ? {
              opacity: 0
            } : {
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="space-y-6">
                  <div className="bg-muted/20 border border-border/50 rounded-xl p-5 space-y-4">
                    <div className="flex items-start justify-between border-b border-border/40 pb-3 gap-3">
                      <div>
                        <span className="font-mono text-xs text-muted-foreground block">
                          {selectedServiceObjs.length > 1 ? "SELECTED SERVICES" : "SELECTED SERVICE"}
                        </span>
                        <div className="space-y-1 mt-1">
                          {selectedServiceObjs.map((s, i) => <h4 key={s.id} data-index={i} className="font-semibold text-base md:text-lg text-foreground flex items-center gap-2">
                              <span className="size-1.5 rounded-full bg-[var(--accent-deep-red)] inline-block shrink-0" />
                              <span>{s.title}</span>
                            </h4>)}
                        </div>
                      </div>
                      <span className="font-mono text-xs bg-[hsl(352_54%_33%/0.1)] text-[var(--accent-deep-red)] border border-[hsl(352_54%_33%/0.3)] px-2.5 py-1 rounded-full shrink-0 self-start">
                        {combinedDuration}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-mono text-xs text-muted-foreground block">DATE & TIME</span>
                        <p className="font-medium text-foreground">{formattedDate} at {selectedTimeSlot}</p>
                      </div>

                      <div>
                        <span className="font-mono text-xs text-muted-foreground block">CONTACT</span>
                        <p className="font-medium text-foreground">{fullName || "Not provided"}</p>
                        <p className="text-xs text-muted-foreground">{email} • {phone}</p>
                      </div>

                      <div>
                        <span className="font-mono text-xs text-muted-foreground block">BUSINESS</span>
                        <p className="font-medium text-foreground">{businessName || "Not provided"}</p>
                        {websiteUrl && <p className="text-xs text-muted-foreground truncate">{websiteUrl}</p>}
                      </div>

                      <div>
                        <span className="font-mono text-xs text-muted-foreground block">LOCATION</span>
                        <p className="font-medium text-foreground">{cityArea || "Not specified"}</p>
                      </div>
                    </div>

                    {notes && <div className="pt-2 border-t border-border/40 text-xs">
                        <span className="font-mono text-muted-foreground block">NOTES</span>
                        <p className="text-foreground/90 italic mt-0.5">&quot;{notes}&quot;</p>
                      </div>}
                  </div>

                  {submitError && <div className="p-4 text-[var(--accent-deep-red)] bg-[hsl(352_54%_33%/0.1)] border border-[hsl(352_54%_33%/0.4)] text-sm rounded-lg">
                      {submitError}
                    </div>}

                  <div className="text-xs text-muted-foreground text-center">
                    By clicking &quot;Confirm & Reserve Call&quot;, you consent to receiving call confirmation details via email and phone.
                  </div>
                </motion.div>}
            </AnimatePresence>
          </Form>
        </CardContent>

        <CardFooter className="border-t border-border/40 pt-5 flex items-center justify-between">
          <Button type="button" variant="outline" onClick={handleBack} disabled={currentStep === 1 || isSubmitting} className="flex items-center gap-1.5">
            <ArrowLeft className="size-4" />
            <span>Back</span>
          </Button>

          {currentStep < 5 ? <Button type="button" variant="default" onClick={handleNext} className="bg-[var(--accent-deep-red)] text-white hover:bg-[hsl(352_54%_28%)] flex items-center gap-1.5">
              <span>Continue</span>
              <ArrowRight className="size-4" />
            </Button> : <Button type="button" variant="default" onClick={() => handleSubmit()} disabled={isSubmitting} className="bg-[var(--accent-deep-red)] text-white hover:bg-[hsl(352_54%_28%)] flex items-center gap-1.5 px-6">
              {isSubmitting ? <span>Scheduling...</span> : <>
                  <span>Confirm &amp; Reserve Call</span>
                  <CheckCircle2 className="size-4" />
                </>}
            </Button>}
        </CardFooter>
      </Card>
    </div>;
}
export default function Booking() {
  return <section id="bookingjourney" data-nav="dark" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-t border-border/40">
      <div className="absolute top-1/4 -left-32 size-96 bg-[hsl(352_54%_33%/0.1)] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 size-96 bg-[hsl(352_54%_33%/0.05)] rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 max-w-5xl relative z-10 space-y-12">
        <div className="text-center space-y-4 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border/80 bg-muted/30 text-xs font-mono tracking-widest text-[var(--accent-deep-red)] uppercase">
            <Sparkles className="size-3.5" />
            <span>ClearLine Booking Journey</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tight text-foreground">
            Schedule Your <span className="font-serif italic font-normal text-[var(--accent-deep-red)]">Strategy Call</span>
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Choose your focus area, pick a time slot, and discover how ClearLine makes your business the obvious answer when local customers ask AI who to hire.
          </p>
        </div>

        <FormProvider formId="booking-journey-interactive" sectionName="booking-journey" defaultValues={{
        services: "",
        businessName: "",
        websiteUrl: "",
        cityArea: "",
        fullName: "",
        email: "",
        phone: "",
        notes: ""
      }} validationRules={{
        fullName: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          minLength: 7
        },
        businessName: {
          required: true
        }
      }} submitText="Confirm & Reserve Call" successMessage="Your call has been scheduled successfully!">
          <BookingFormInner />
        </FormProvider>

        <div className="pt-8 border-t border-border/40 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center text-xs font-mono text-muted-foreground">
          <div className="flex items-center justify-center gap-2">
            <ShieldCheck className="size-4 text-[var(--accent-deep-red)]" />
            <span>100% Free Visibility Audit</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Clock className="size-4 text-[var(--accent-deep-red)]" />
            <span>48-Hour Guarantee</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <CheckCircle2 className="size-4 text-[var(--accent-deep-red)]" />
            <span>No Obligation Required</span>
          </div>
        </div>
      </div>
    </section>;
}